import { Int } from '@adornis/baseql/baseqlTypes.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerMutation, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import { context, publishFilter, serverContext } from '@adornis/baseql/server/context.js';
import { runInContext } from '@adornis/baseql/server/server.js';
import { watchEntireCollection } from '@adornis/baseql/server/watchQuery.js';
import { type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { MailTemplate, type MailVarContents } from '@adornis/mails/db/mail-template.js';
import { Recipient } from '@adornis/mails/db/recipients.js';
import { Message } from '@adornis/mails/server/communication.js';
import { assignRoleToUser } from '@adornis/users/server/roles.js';
import { generateLoginToken } from '@adornis/users/server/token.js';
import { concatMap, firstValueFrom, from, merge, of, switchMap, timer } from 'rxjs';
import { BHFilter } from '../entities/bh-filter.js';
import { Institution } from '../entities/institution.js';
import { GlobalMailTemplate, InstitutionMailTemplate, MailTemplateVars } from '../entities/mail-templates.js';
import type { Participation } from '../entities/Participation.js';
import { UserStammdaten } from '../entities/stammdaten.js';
import { User } from '../entities/user.js';
import { Role } from '../enums/roles.js';
import { mailer } from '../mails/communication.js';
import { notifyNewsletterOptIn } from '../notification/newsletter-opt-in.js';
import { getPluginForScope } from '../plugins/plugin.js';
import { getNewsletterOptInSecret } from '../server/logic/newsletter.js';
import { searchMatch } from '../util.js';

export const sendFreeFormMail = registerSubscription({
  operationName: 'sendFreeFormMail',
  resolve: (mailAddresses: string[], mailTemplate: GlobalMailTemplate | InstitutionMailTemplate) => () => {
    return from(
      new Promise<Array<EntityData<User>>>(async resolve => {
        if (mailTemplate instanceof InstitutionMailTemplate && mailTemplate.institutionID) {
          const institution = await getByID(Institution, mailTemplate.institutionID)(Institution.allFields);
          mailTemplate.institution = institution as Institution;
        }

        const users = await (await getCollection<User>(User._class)).find({ email: { $in: mailAddresses } }).toArray();
        if (users.length !== Array.from(new Set(mailAddresses)).length)
          throw new Error(
            `Couldnt find users to some mail addresses (mailAddressesLength: ${mailAddresses.length}; usersLength: ${
              users.length
            }; ${mailAddresses.filter(mail => !users.map(x => x.email).includes(mail))})`,
          );

        return resolve(users);
      }),
    ).pipe(
      concatMap(users => {
        const tasks = users.map((user, i) => ({ id: i.toString(), user }));
        return merge(...tasks.map((t, i) => of(t)));
      }),
      concatMap(async ({ id, user }) => {
        const token = await generateLoginToken({
          userID: user._id,
          userClass: User._class,
          ttl: 1000 * 60 * 60 * 24 * 7 /* 7 days */,
        });
        const vars = {
          [MailTemplateVars.FIRST_NAME]: user.firstName,
          [MailTemplateVars.LAST_NAME]: user.lastName,
          [MailTemplateVars.TITLE]: user.title,
          [MailTemplateVars.INSTITUTION_NAME]: (mailTemplate as InstitutionMailTemplate).institution?.name,
          [MailTemplateVars.LOGIN_LINK]: InstitutionMailTemplate.mapLoginLinkVar(token),
        } as MailVarContents;

        const html = await mailTemplate.buildEmail(vars);
        const subject = await mailTemplate.buildSubject(vars);

        if (!subject) throw new Error('Bitte tragen Sie einen Betreff ein');
        if (!html) throw new Error('Bitte tragen Sie den Inhalt ein');

        await (await mailer).sendMail(await Message.compose({ html, subject }), new Recipient(user.email));

        // dont bulk send all emails, especially for larger amounts
        await firstValueFrom(timer(100));

        return id;
      }),
    );
  },
  type: () => String,
  params: [
    { name: 'mailAddresses', type: () => [String] },
    { name: 'mailTemplate', type: () => MailTemplate },
  ],
});

export const subscribeParticipantsByInstitutionPaginated = registerSubscription({
  operationName: 'subscribeParticipantsByInstitutionPaginated',
  resolve: (skip: number, limit: number, filter?: BHFilter) => {
    return (gqlFields: BaseQLSelectionSet<User>) => {
      skip ??= 0;
      limit ??= 9999999;

      const institutionID = filter?.institutionID;
      if (!institutionID) throw new Error('Missing institutionID');

      return watchEntireCollection(`${User._collectionName}-materialized`).pipe(
        switchMap(async () => {
          const collection = await getRawCollection<Participation>(`${User._collectionName}-materialized`);
          let pF = await publishFilter(User._class);
          if (context.serverContext) pF = {};
          if (!pF) throw new Error(`Missing publishFilter for ${User._class}`);

          const participationMatch = {
            $filter: {
              input: '$participations',
              as: 'item',
              cond: { $eq: ['$$item.userStammdaten.institutionID', institutionID] },
            },
          };

          const timeframeMatch = filter.timeframeID
            ? [{ $match: { 'participations.product.timeframeID': filter?.timeframeID } }]
            : [];

          const aggregation = [
            ...timeframeMatch,
            { $set: { participation: { $arrayElemAt: [participationMatch, 0] } } },
            {
              $set: {
                stammdaten: '$participation.userStammdaten',
                institutionID: '$participation.product.institutionID',
                timeframeID: '$participation.product.timeframeID',
              },
            },
            { $match: { $and: [pF, filter?.getMongoFilter() ?? {}] } },
            { $match: searchMatch(['firstName', 'lastName', 'email'], filter.search) },
            { $sort: filter?.getMongoOptions().sort ?? {} },
            { $project: gqlFields },
            { $skip: skip },
            { $limit: limit },
          ];

          return await collection.aggregate(aggregation).toArray();
        }),
      );
    };
  },
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'filter', type: () => BHFilter },
  ],
  type: () => [User],
});

export const createUserForInstitution = registerMutation({
  operationName: 'createUserForInstitution',
  params: [
    { name: 'institutionID', type: () => String },
    { name: 'user', type: () => User },
    { name: 'type', type: () => String },
    { name: 'stammdaten', type: () => UserStammdaten },
  ],
  type: () => User,
  resolve: (institutionID: string, user: User, type: 'user' | 'lecturer', stammdaten?: UserStammdaten) => {
    return async (gqlFields: BaseQLSelectionSet<User>) => {
      if (!institutionID) throw new Error('Missing institutionID');
      const userCollection = await getRawCollection(User._collectionName);
      const stammdatenCollection = await getRawCollection(UserStammdaten._collectionName);

      user = new User(user.toJSON());

      const userID = await (async () => {
        if (user._id) {
          const existingUser = await getByID(User, user._id)({ _id: 1 });
          if (existingUser) return existingUser._id;
        }

        // NOTE: this can happen when the user already exists but only participated in workshops of other institutions.
        //       In this case the admin cant know that the user already exists
        const mailRegex = { $regex: `^${user.email.replace(/([^\w]{1})/g, '\\$1')}$`, $options: 'i' };
        const existingUserByMail = await userCollection.findOne({ email: mailRegex });
        if (existingUserByMail) {
          user._id = existingUserByMail._id.toString();
          return existingUserByMail._id.toString();
        }

        return await user.create();
      })();

      await runInContext(serverContext.sessionID, async () => {
        // add role with institution context, so that publish filter do not prevent admins from seeing the user
        if (type === 'user') {
          await User.ensureSelfRoles(userID);
          await assignRoleToUser(userID, Role.PARTICIPANT, [institutionID], User);
        }

        if (type === 'lecturer') await assignRoleToUser(userID, Role.LECTURER, [institutionID], User);

        if (stammdaten) {
          const existingStammdaten = await stammdatenCollection.findOne({ userID, institutionID });
          if (existingStammdaten) {
            // in case stammdaten already exist just update them
            if (stammdaten.userID !== existingStammdaten.userID) throw new Error('should not happen 1');
            if (stammdaten.institutionID !== existingStammdaten.institutionID) throw new Error('should not happen 2');
            stammdaten.save();
          } else {
            stammdaten.userID = userID; // userID might have changed
            const stammdatenPlugin = getPluginForScope('registration::stammdaten', institutionID);
            const newStammdaten = await stammdatenPlugin.createStammdaten({ userID, institutionID, createInDB: false });
            for (const [key, value] of Object.entries(stammdaten.toJSON())) newStammdaten[key] = value;
            await newStammdaten.create();
          }
        }
      });

      user._id = userID;
      return user;
    };
  },
});

export const sendUserNewsletterOptInMail = registerMutation({
  operationName: 'sendUserNewsletterOptInMail',
  params: [{ name: 'userID', type: () => String }],
  type: () => Int,
  resolve: (userID: string) => {
    return async () => {
      await notifyNewsletterOptIn(userID);

      return 1;
    };
  },
});

export const handleNesletterOptInReuest = registerMutation({
  operationName: 'handleNesletterOptInReuest',
  type: () => Boolean,
  params: [
    { name: 'userID', type: () => String },
    { name: 'token', type: () => String },
  ],
  resolve: (userID: string, token: string) => async () => {
    // check for valid token
    const secret = getNewsletterOptInSecret(userID);
    if (secret !== token) throw new Error('invalid_token');

    const userCollection = await getRawCollection(User._collectionName);
    await userCollection.updateOne({ _id: userID as any }, { $set: { receivesNewsletter: true } });

    return true;
  },
});
